import {
  MAX_OVERCHARGE,
  OVERCHARGE_PER_MERGE,
  OVERCHARGE_PER_CASCADE_STEP,
  SPLINTER_OVERCHARGE_COST,
  OVERCHARGE_STARTING,
} from '../config';

/**
 * Callback fired whenever the overcharge value changes.
 */
export type OverchargeChangeCallback = (charge: number, max: number) => void;

/**
 * OverchargeSystem powers the Splinter mechanic with an in-run resource that
 * builds through gameplay (merges + cascades), not real-world timers.
 *
 * Why: tying a survival mechanic to real-world energy refills caused players
 * to hit a paywall mid-run when they most needed to splinter. Overcharge lets
 * smart play earn the right to splinter, keeping the core loop frictionless.
 */
export class OverchargeSystem {
  private charge: number;
  private maxCharge: number;
  private onChange?: OverchargeChangeCallback;

  constructor() {
    this.charge = OVERCHARGE_STARTING;
    this.maxCharge = MAX_OVERCHARGE;
  }

  /** Current overcharge value (0..max). */
  getCharge(): number {
    return this.charge;
  }

  /** Maximum overcharge value. */
  getMaxCharge(): number {
    return this.maxCharge;
  }

  /** Fraction full (0..1) — useful for HUD bars. */
  getFraction(): number {
    return this.maxCharge === 0 ? 0 : this.charge / this.maxCharge;
  }

  /** Can the player afford a splinter right now? */
  canSplinter(): boolean {
    return this.charge >= SPLINTER_OVERCHARGE_COST;
  }

  /** Spend the splinter cost. Returns true on success, false if insufficient. */
  spendSplinter(): boolean {
    if (!this.canSplinter()) return false;
    this.charge -= SPLINTER_OVERCHARGE_COST;
    this.notifyChange();
    return true;
  }

  /**
   * Award charge for a single merge. Capped at max.
   */
  awardMerge(): void {
    this.charge = Math.min(this.charge + OVERCHARGE_PER_MERGE, this.maxCharge);
    this.notifyChange();
  }

  /**
   * Award charge for a cascade step (chain step >= 2 — i.e. an actual cascade,
   * not the initial player-driven merge).
   */
  awardCascade(): void {
    this.charge = Math.min(this.charge + OVERCHARGE_PER_CASCADE_STEP, this.maxCharge);
    this.notifyChange();
  }

  /** Reset overcharge for a new run. */
  reset(): void {
    this.charge = OVERCHARGE_STARTING;
    this.notifyChange();
  }

  /** Set charge directly (testing / debugging). Clamped to [0, max]. */
  setCharge(amount: number): void {
    this.charge = Math.max(0, Math.min(amount, this.maxCharge));
    this.notifyChange();
  }

  /** Subscribe to charge changes. */
  setOnChange(callback: OverchargeChangeCallback): void {
    this.onChange = callback;
  }

  private notifyChange(): void {
    if (this.onChange) this.onChange(this.charge, this.maxCharge);
  }
}
